import { InfoCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Input, overlayStyle, Stack, Switch, Text, Tooltip } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/LegacyToast';
import React, { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { isSsoAutoLoginEnabled } from '../../../../common/auth/ssoAutoLogin/state';
import {
  certificateStatusFor,
  confirmReplaceCertificate,
  generateAndDownloadCertificate,
  generateTooltipFor,
  signingTooltipFor
} from './logoutSigning';
import { ComponentProps } from './types';

export default function SamlSingleLogoutFields(props: ComponentProps) {
  const { control, errors, savedSloEnabled, signingConnectorId, hasLogoutSigningKey, refreshSigningState } = props;
  const [isGenerating, setIsGenerating] = useState(false);

  // The URL requirement follows the form; the rest follows saved state, because generating a
  // certificate reaches the server before Save.
  const isSloEnabled = Boolean(useWatch({ control, name: 'saml.sloEnabled' }));
  const isSloSaved = Boolean(savedSloEnabled);
  const hasCertificate = Boolean(hasLogoutSigningKey);
  const canSignLogout = isSloSaved && hasCertificate;
  const canGenerate = isSloSaved && Boolean(signingConnectorId);

  const onGenerateCertificate = async () => {
    if (!signingConnectorId) return;
    if (hasCertificate && !(await confirmReplaceCertificate())) return;

    setIsGenerating(true);
    const error = await generateAndDownloadCertificate(signingConnectorId);
    setIsGenerating(false);

    if (error) {
      message.error({ content: error });
      return;
    }
    message.success({ content: 'Signature certificate generated and downloaded.' });
    refreshSigningState?.();
  };

  return (
    <>
      <Controller
        render={({ field }) => (
          <Stack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
            <Switch {...field} checked={Boolean(field.value)} data-test-id='saml-auth-slo-enabled-switch'>
              Enable Single Logout
            </Switch>
            <Tooltip
              overlayStyle={overlayStyle}
              title='When on, signing out of Prophecy also signs the user out of your identity provider'>
              <div>
                <InfoCircleIcon type='default' color={theme.colors.gray500} />
              </div>
            </Tooltip>
          </Stack>
        )}
        control={control}
        name='saml.sloEnabled'
        rules={{ deps: ['saml.ssoLogoutURL'] }}
      />
      {!isSsoAutoLoginEnabled() && (
        <Text level='xs' tone={theme.colors.gray500} data-test-id='saml-auth-slo-disabled-note'>
          Single Logout is turned off for this deployment, so these settings have no effect.
        </Text>
      )}
      <Controller
        render={({ field }) => (
          <Input
            label={
              <Stack gap={theme.spaces.x4} direction='horizontal'>
                Single Logout URL
                <Tooltip
                  overlayStyle={overlayStyle}
                  title="Your identity provider's Single Logout endpoint, from its metadata. Required to enable Single Logout.">
                  <div>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                  </div>
                </Tooltip>
              </Stack>
            }
            {...field}
            value={field.value ?? ''}
            disabled={!isSloEnabled}
            required={isSloEnabled}
            variant={errors?.saml?.ssoLogoutURL ? 'error' : 'primary'}
            helpText={errors?.saml?.ssoLogoutURL?.message}
            data-test-id='saml-auth-slo-url-input'
          />
        )}
        control={control}
        name='saml.ssoLogoutURL'
        rules={{
          validate: (value, formValues) =>
            !formValues.saml?.sloEnabled || Boolean(value) || 'Single Logout URL is required'
        }}
      />
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x16}>
        <Controller
          render={({ field }) => (
            <Switch
              {...field}
              // Never show it on when it cannot work; the saved value is left alone.
              checked={canSignLogout && Boolean(field.value)}
              disabled={!canSignLogout}
              tooltip={signingTooltipFor(isSloSaved, hasCertificate)}
              data-test-id='saml-auth-sign-logout-switch'>
              Sign single logout (SLO) messages
            </Switch>
          )}
          control={control}
          name='saml.signLogoutRequests'
        />
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          <Button
            size='s'
            variant='secondaryGray'
            disabled={!canGenerate}
            loading={isGenerating}
            tooltip={generateTooltipFor(isSloSaved)}
            onClick={onGenerateCertificate}
            data-test-id='saml-auth-generate-signing-cert-button'>
            Generate Signature Certificate
          </Button>
          <Text level='xs' tone={theme.colors.gray500} data-test-id='saml-auth-signing-cert-status'>
            {certificateStatusFor(isSloSaved, hasCertificate)}
          </Text>
        </Stack>
      </Stack>
    </>
  );
}
